import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import {
  alpha,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material'
import { baseUrl } from 'api/config'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

interface RoadmapConfigFormProps {
  title: string
  onSubmit: (message: string) => void
  isInteractive: boolean
  organizations: any[]
}

const DEAL_TYPES = [
  { value: 'MSA', label: 'MSA', desc: 'Master Service Agreement' },
  { value: 'CSA', label: 'CSA', desc: 'Creative Service Agreement' },
  { value: 'IO', label: 'IO', desc: 'Insertion Order' },
]

const DURATIONS = [
  { value: 1, label: '1 Month', rate: '$240/pt' },
  { value: 3, label: '3 Months', rate: '$235/pt' },
  { value: 6, label: '6 Months', rate: '$220/pt' },
  { value: 12, label: '12 Months', rate: '$200/pt' },
]

const SERVICE_LINES = [
  { value: 'seo', label: 'SEO', color: '#4AA17B' },
  { value: 'ppc', label: 'Paid Media', color: '#F59E0B' },
  { value: 'social', label: 'Social Media', color: '#8B5CF6' },
  { value: 'creative', label: 'Creative', color: '#EC4899' },
  { value: 'ana', label: 'Analytics', color: '#3498DB' },
  { value: 'emm', label: 'Email Marketing', color: '#E91E90' },
  { value: 'ux', label: 'UX/Design', color: '#DAA520' },
  { value: 'web', label: 'Web Dev', color: '#EF4444' },
  { value: 'int', label: 'Integration', color: '#06B6D4' },
]

const BUDGET_PRESETS = [
  { value: 3000, label: '$3,000' },
  { value: 5000, label: '$5,000' },
  { value: 7500, label: '$7,500' },
  { value: 10000, label: '$10,000' },
  { value: 15000, label: '$15,000' },
  { value: 20000, label: '$20,000' },
]

const OBJECTIVES = [
  'Grow Organic Traffic',
  'Improve Paid Performance',
  'Better Analytics & Insights',
  'Email Marketing Strategy',
  'Website Redesign / UX',
  'Brand Awareness',
  'Lead Generation',
  'Local SEO / Maps',
  'Content Strategy',
  'Conversion Rate Optimization',
]

const steps = ['Client & Budget', 'Services & Goals']

// Persist form state across dock/resize remounts
const FORM_STATE_KEY = 'roadmapConfigForm'

function loadFormState() {
  try {
    const raw = sessionStorage.getItem(FORM_STATE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

const RoadmapConfigForm: React.FC<RoadmapConfigFormProps> = ({ title, onSubmit, isInteractive, organizations }) => {
  const theme = useTheme()
  const saved = useRef(loadFormState())

  const [activeStep, setActiveStep] = useState(saved.current?.activeStep ?? 0)
  const [submitted, setSubmitted] = useState(saved.current?.submitted ?? false)

  // Step 1: Client & Budget
  const [selectedOrg, setSelectedOrg] = useState<any>(saved.current?.selectedOrg ?? null)
  const [dealType, setDealType] = useState(saved.current?.dealType ?? 'MSA')
  const [clientContext, setClientContext] = useState(saved.current?.clientContext ?? '')
  const [duration, setDuration] = useState(saved.current?.duration ?? 12)
  const [budgetMode, setBudgetMode] = useState<'preset' | 'custom'>(saved.current?.budgetMode ?? 'preset')
  const [budget, setBudget] = useState(saved.current?.budget ?? 5000)
  const [customBudget, setCustomBudget] = useState(saved.current?.customBudget ?? '')

  // Website analysis
  const [websiteUrl, setWebsiteUrl] = useState(saved.current?.websiteUrl ?? '')
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analysisSummary, setAnalysisSummary] = useState(saved.current?.analysisSummary ?? '')

  // Step 2: Services & Goals
  const [serviceLines, setServiceLines] = useState<string[]>(saved.current?.serviceLines ?? [])
  const [objectives, setObjectives] = useState<string[]>(saved.current?.objectives ?? [])

  // Persist form state on every change
  useEffect(() => {
    if (submitted) {
      sessionStorage.removeItem(FORM_STATE_KEY)
      return
    }
    try {
      sessionStorage.setItem(
        FORM_STATE_KEY,
        JSON.stringify({
          activeStep, selectedOrg, dealType, clientContext, duration,
          budgetMode, budget, customBudget, websiteUrl, analysisSummary,
          serviceLines, objectives, submitted,
        })
      )
    } catch { /* sessionStorage full */ }
  }, [activeStep, selectedOrg, dealType, clientContext, duration, budgetMode, budget, customBudget, websiteUrl, analysisSummary, serviceLines, objectives, submitted])

  const orgOptions = useMemo(() => {
    if (!organizations?.length) return []
    return organizations
      .filter(o => o?.name)
      .map(o => ({ PK: o.PK, name: o.name, website: o.website || '' }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [organizations])

  const effectiveBudget = budgetMode === 'custom' ? Number(customBudget) || 0 : budget
  const rateInfo = DURATIONS.find(d => d.value === duration)

  // Map analysis objectives to our OBJECTIVES list
  const OBJECTIVE_KEYWORDS: Record<string, string[]> = {
    'Grow Organic Traffic': ['organic', 'seo', 'search engine'],
    'Improve Paid Performance': ['paid', 'ppc', 'advertising', 'ads', 'google ads'],
    'Better Analytics & Insights': ['analytics', 'data', 'insights', 'tracking', 'reporting'],
    'Email Marketing Strategy': ['email', 'newsletter', 'drip', 'automation'],
    'Website Redesign / UX': ['website', 'redesign', 'ux', 'user experience', 'design'],
    'Brand Awareness': ['brand', 'awareness', 'visibility'],
    'Lead Generation': ['lead', 'conversion', 'generate'],
    'Local SEO / Maps': ['local', 'maps', 'gmb', 'google business'],
    'Content Strategy': ['content', 'blog', 'copywriting'],
    'Conversion Rate Optimization': ['conversion rate', 'cro', 'optimize'],
  }

  const handleAnalyzeWebsite = async () => {
    const url = websiteUrl.trim()
    if (!url) return
    setIsAnalyzing(true)
    setAnalysisSummary('')
    try {
      const resp = await fetch(`${baseUrl}/api/agent/analyze-website`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      })
      const json = await resp.json()
      if (json.success && json.data) {
        const d = json.data

        // Auto-fill client context from analysis
        const contextParts: string[] = []
        if (d.industry) contextParts.push(d.industry)
        if (d.businessModel) contextParts.push(`Business model: ${d.businessModel}`)
        if (d.clientSize) contextParts.push(`Size: ${d.clientSize}`)
        if (contextParts.length > 0) setClientContext(contextParts.join(' | '))

        // Auto-select matching objectives
        if (d.suggestedObjectives?.length) {
          const matched: string[] = []
          for (const suggested of d.suggestedObjectives) {
            const lower = suggested.toLowerCase()
            // Direct match first
            if (OBJECTIVES.includes(suggested)) {
              matched.push(suggested)
              continue
            }
            // Keyword match
            for (const [obj, keywords] of Object.entries(OBJECTIVE_KEYWORDS)) {
              if (keywords.some(kw => lower.includes(kw))) {
                matched.push(obj)
                break
              }
            }
          }
          if (matched.length > 0) setObjectives([...new Set(matched)])
        }

        // Auto-select service lines based on analysis
        if (d.suggestedObjectives?.length) {
          const suggestedLines: string[] = []
          const allSuggested = d.suggestedObjectives.join(' ').toLowerCase()
          if (allSuggested.match(/seo|organic|search/)) suggestedLines.push('seo')
          if (allSuggested.match(/ppc|paid search|google ads|display|programmatic|media spend/))
            suggestedLines.push('ppc')
          if (allSuggested.match(/social|facebook|instagram|tiktok|linkedin/)) suggestedLines.push('social')
          if (allSuggested.match(/creative|photo|video|brand|design|logo/)) suggestedLines.push('creative')
          if (allSuggested.match(/analytics|data|tracking/)) suggestedLines.push('ana')
          if (allSuggested.match(/email|newsletter/)) suggestedLines.push('emm')
          if (allSuggested.match(/design|ux|conversion rate/)) suggestedLines.push('ux')
          if (allSuggested.match(/web|development|build/)) suggestedLines.push('web')
          if (allSuggested.match(/integration|gtm|ga4|call tracking/)) suggestedLines.push('int')
          if (suggestedLines.length > 0) setServiceLines([...new Set(suggestedLines)])
        }

        if (d.summary) setAnalysisSummary(d.summary)
      }
    } catch (err) {
      console.error('Website analysis failed:', err)
      setAnalysisSummary('Analysis failed — you can still configure manually.')
    } finally {
      setIsAnalyzing(false)
    }
  }

  const handleServiceToggle = (val: string) => {
    setServiceLines(prev => (prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]))
  }

  const handleObjectiveToggle = (val: string) => {
    setObjectives(prev => (prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]))
  }

  const handleSubmit = () => {
    const parts: string[] = ['Build a roadmap with the following configuration:']

    parts.push(`Client: ${selectedOrg.name} (${selectedOrg.PK}).`)
    parts.push(`Deal type: ${dealType}.`)
    if (websiteUrl.trim()) {
      parts.push(`Website: ${websiteUrl.trim()}.`)
    }
    if (clientContext) {
      parts.push(`Client context: ${clientContext}.`)
    }
    if (analysisSummary) {
      parts.push(`Website analysis: ${analysisSummary}`)
    }

    parts.push(`Duration: ${duration} months.`)
    parts.push(`Monthly budget: $${effectiveBudget.toLocaleString()}.`)

    if (serviceLines.length > 0) {
      parts.push(`Service lines: ${serviceLines.join(', ')}.`)
    } else {
      parts.push('Service lines: all available.')
    }

    if (objectives.length > 0) {
      parts.push(`Objectives: ${objectives.join(', ')}.`)
    }

    setSubmitted(true)
    sessionStorage.removeItem(FORM_STATE_KEY)
    // Save original config for the revision form
    try {
      sessionStorage.setItem('roadmapOriginalConfig', JSON.stringify({
        serviceLines,
        objectives,
        duration,
        budget: effectiveBudget,
        dealType,
        clientName: selectedOrg?.name,
      }))
    } catch { /* ignore */ }
    onSubmit(parts.join(' '))
  }

  if (!isInteractive || submitted) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          my: 0.5,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          maxWidth: 420,
          opacity: 0.8,
        }}
      >
        {submitted && <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />}
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
          {submitted
            ? `${dealType}: ${selectedOrg?.name} — $${effectiveBudget.toLocaleString()}/mo × ${duration}mo`
            : title}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        px: 1.5,
        py: 1.5,
        my: 0.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        maxWidth: 460,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
        {title}
      </Typography>

      <Stepper activeStep={activeStep} alternativeLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.7rem' } }}>
        {steps.map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step 0: Client & Budget */}
      {activeStep === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Autocomplete
            size="small"
            options={orgOptions}
            getOptionLabel={o => o.name}
            value={selectedOrg}
            onChange={(_, val) => {
              setSelectedOrg(val)
              if (val?.website && !websiteUrl) setWebsiteUrl(val.website)
            }}
            renderInput={params => (
              <TextField
                {...params}
                label="Client *"
                placeholder="Search organizations..."
                error={!selectedOrg}
                sx={{
                  '& .MuiInputBase-root': { fontSize: '0.8rem' },
                  '& .MuiInputLabel-root': { fontSize: '0.8rem' },
                }}
              />
            )}
          />

          {/* Deal Type */}
          <Box>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 0.5 }}>Deal Type</Typography>
            <ToggleButtonGroup
              value={dealType}
              exclusive
              onChange={(_, val) => val && setDealType(val)}
              size="small"
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
            >
              {DEAL_TYPES.map(t => (
                <ToggleButton
                  key={t.value}
                  value={t.value}
                  sx={{
                    flex: '1 0 auto',
                    fontSize: '0.6rem',
                    py: 0.5,
                    px: 1,
                    textTransform: 'none',
                    borderRadius: '8px !important',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ fontWeight: 700, fontSize: '0.65rem' }}>{t.label}</Box>
                    <Box sx={{ fontSize: '0.5rem', color: 'text.secondary' }}>{t.desc}</Box>
                  </Box>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* Website Analysis */}
          <Box>
            <TextField
              size="small"
              label="Client Website"
              placeholder="https://example.com"
              value={websiteUrl}
              onChange={e => setWebsiteUrl(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && websiteUrl.trim()) {
                  e.preventDefault()
                  handleAnalyzeWebsite()
                }
              }}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      onClick={handleAnalyzeWebsite}
                      disabled={!websiteUrl.trim() || isAnalyzing}
                      sx={{
                        fontSize: '0.6rem',
                        textTransform: 'none',
                        minWidth: 0,
                        px: 1,
                        py: 0.25,
                        fontWeight: 600,
                        color: 'text.ai_agent_text',
                        gap: 0.5,
                      }}
                    >
                      {isAnalyzing ? <CircularProgress size={12} /> : <AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                      {isAnalyzing ? 'Analyzing...' : 'Analyze'}
                    </Button>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputBase-root': { fontSize: '0.8rem' },
                '& .MuiInputLabel-root': { fontSize: '0.8rem' },
              }}
            />
            {analysisSummary && (
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  fontStyle: 'italic',
                  color: 'text.secondary',
                  mt: 0.5,
                  px: 0.5,
                  lineHeight: 1.4,
                }}
              >
                {analysisSummary}
              </Typography>
            )}
          </Box>

          <TextField
            size="small"
            label="Client Context (optional)"
            placeholder="Industry, company size, business model..."
            value={clientContext}
            onChange={e => setClientContext(e.target.value)}
            multiline
            maxRows={2}
            sx={{
              '& .MuiInputBase-root': { fontSize: '0.8rem' },
              '& .MuiInputLabel-root': { fontSize: '0.8rem' },
            }}
          />

          {/* Duration */}
          <Box>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 0.5 }}>Engagement Duration</Typography>
            <ToggleButtonGroup
              value={duration}
              exclusive
              onChange={(_, val) => val && setDuration(val)}
              size="small"
              sx={{ display: 'flex', gap: 0.5 }}
            >
              {DURATIONS.map(d => (
                <ToggleButton
                  key={d.value}
                  value={d.value}
                  sx={{
                    flex: 1,
                    fontSize: '0.65rem',
                    py: 0.5,
                    px: 1,
                    textTransform: 'none',
                    borderRadius: '8px !important',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ fontWeight: 700 }}>{d.label}</Box>
                    <Box sx={{ fontSize: '0.55rem', color: 'text.secondary' }}>{d.rate}</Box>
                  </Box>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* Budget */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Monthly Budget</Typography>
              <Button
                size="small"
                onClick={() => setBudgetMode(budgetMode === 'preset' ? 'custom' : 'preset')}
                sx={{ fontSize: '0.6rem', textTransform: 'none', minWidth: 0, p: 0 }}
              >
                {budgetMode === 'preset' ? 'Enter custom' : 'Use presets'}
              </Button>
            </Box>

            {budgetMode === 'preset' ? (
              <ToggleButtonGroup
                value={budget}
                exclusive
                onChange={(_, val) => val && setBudget(val)}
                size="small"
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
              >
                {BUDGET_PRESETS.map(b => (
                  <ToggleButton
                    key={b.value}
                    value={b.value}
                    sx={{
                      flex: '1 0 30%',
                      fontSize: '0.7rem',
                      py: 0.5,
                      textTransform: 'none',
                      borderRadius: '8px !important',
                      border: '1px solid',
                      borderColor: 'divider',
                      fontWeight: 600,
                    }}
                  >
                    {b.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            ) : (
              <TextField
                size="small"
                label="Monthly Budget ($)"
                type="number"
                value={customBudget}
                onChange={e => setCustomBudget(e.target.value)}
                fullWidth
                sx={{
                  '& .MuiInputBase-root': { fontSize: '0.8rem' },
                  '& .MuiInputLabel-root': { fontSize: '0.8rem' },
                }}
              />
            )}
          </Box>
        </Box>
      )}

      {/* Step 1: Services & Goals */}
      {activeStep === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Service Lines */}
          <Box>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 0.5 }}>
              Service Lines (select all that apply, or leave empty for all)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {SERVICE_LINES.map(s => {
                const selected = serviceLines.includes(s.value)
                return (
                  <Chip
                    key={s.value}
                    label={s.label}
                    size="small"
                    onClick={() => handleServiceToggle(s.value)}
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      borderRadius: '8px',
                      border: '1px solid',
                      borderColor: selected ? s.color : 'divider',
                      bgcolor: selected ? `${s.color}18` : 'transparent',
                      color: selected ? s.color : 'text.secondary',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: `${s.color}28` },
                    }}
                  />
                )
              })}
            </Box>
          </Box>

          {/* Objectives */}
          <Box>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 0.5 }}>
              Client Objectives (select all that apply)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {OBJECTIVES.map(obj => {
                const selected = objectives.includes(obj)
                return (
                  <Chip
                    key={obj}
                    label={obj}
                    size="small"
                    onClick={() => handleObjectiveToggle(obj)}
                    sx={{
                      fontSize: '0.65rem',
                      borderRadius: '8px',
                      border: '1px solid',
                      borderColor: selected ? 'primary.main' : 'divider',
                      bgcolor: selected ? alpha(theme.palette.text.ai_accent_secondary, 0.1) : 'transparent',
                      color: selected ? 'primary.main' : 'text.secondary',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: alpha(theme.palette.text.ai_accent_secondary, 0.15) },
                    }}
                  />
                )
              })}
            </Box>
          </Box>

          {/* Summary */}
          <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, mb: 0.5 }}>Summary</Typography>
            <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
              {selectedOrg?.name} — {dealType}
              {clientContext && ` — ${clientContext}`}
            </Typography>
            <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
              {duration} months × ${effectiveBudget.toLocaleString()}/mo = $
              {(effectiveBudget * duration).toLocaleString()} total ({rateInfo?.rate})
            </Typography>
            <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
              Services:{' '}
              {serviceLines.length > 0
                ? serviceLines.map(s => SERVICE_LINES.find(sl => sl.value === s)?.label).join(', ')
                : 'All available'}
              {objectives.length > 0 && ` | Goals: ${objectives.join(', ')}`}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Button
          size="small"
          onClick={() => setActiveStep(s => s - 1)}
          disabled={activeStep === 0}
          sx={{ fontSize: '0.7rem', visibility: activeStep === 0 ? 'hidden' : 'visible' }}
        >
          Back
        </Button>
        {activeStep < steps.length - 1 ? (
          <Button
            size="small"
            variant="outlined"
            onClick={() => setActiveStep(s => s + 1)}
            disabled={!selectedOrg || effectiveBudget <= 0}
            sx={{
              fontSize: '0.7rem',
              fontWeight: 500,
              borderColor: 'text.ai_agent_text',
              color: 'text.ai_agent_text',
              '&:hover': { borderColor: 'text.highlight', color: 'text.primary' },
            }}
          >
            Next
          </Button>
        ) : (
          <Button
            size="small"
            variant="outlined"
            onClick={handleSubmit}
            disabled={effectiveBudget <= 0}
            sx={{
              fontSize: '0.7rem',
              fontWeight: 600,
              borderColor: 'text.ai_agent_text',
              color: 'text.ai_agent_text',
              '&:hover': { borderColor: 'text.highlight', color: 'text.primary' },
            }}
          >
            Build Roadmap
          </Button>
        )}
      </Box>
    </Box>
  )
}

export default RoadmapConfigForm
